import React, { forwardRef, useState, useEffect, useContext } from 'react'
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControlLabel,
  Grid,
  Grow,
  IconButton,
  Typography,
} from '@mui/material'
import { IntakeContext } from 'context/IntakeContext'
import { motion } from 'framer-motion'
import { Close } from '@mui/icons-material'
import { toast } from 'react-toastify'
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'
import { callZapier } from 'utils/zapier/callZapier'
import { apiClient } from 'utils/api/apiClient'
import { signUpUser } from 'utils/aws/authService'
import { useNavigate } from 'react-router-dom'
import { useApi } from 'hooks/useApi'
import axios from 'axios'

const Transition = forwardRef(function Transition(props, ref) {
  return <Grow timeout={300} ref={ref} {...props} />
})

export default function CreateUserModal({
  onClose,
  title = 'Create a New User',
  buttonText = 'Create User',
}) {
  const { intakeState, assumeUserAccount, adminDetails } =
    useContext(IntakeContext)
  const [sendEmail, setSendEmail] = useState()
  const checked = sendEmail || false
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [isDisabled, setIsDisabled] = useState(true)
  const [fieldErrors, setFieldErrors] = useState({ email: false, phone: false })
  const [isCreatingUser, setIsCreatingUser] = useState(false)
  const { apiRequest } = useApi()
  const navigate = useNavigate()

  useEffect(() => {
    const emailValid = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(
      email
    )
    const shouldDisable =
      !firstName ||
      !lastName ||
      !emailValid ||
      fieldErrors.email ||
      !/\b\d{10}\b/.test(phone) ||
      fieldErrors.phone

    setIsDisabled(shouldDisable)
  }, [firstName, lastName, email, phone, fieldErrors.email, fieldErrors.phone])

  const checkUserExists = async ({ email, phone }) => {
    try {
      const res = await apiRequest('/api/checkUserExists', {
        method: 'POST',
        body: JSON.stringify({ email, phone }),
      })
      const data = await res.json()
      return data
    } catch (err) {
      console.error('User check failed', err)
      return { exists: false }
    }
  }

  const handleEmailBlur = async (email) => {
    if (!email) return
    //possibly standardize lowercase for check
    const lowercasedEmail = email.toLowerCase()
    const { exists, conflict } = await checkUserExists({ email })
    if (exists && conflict === 'email') {
      setFieldErrors((prev) => ({
        ...prev,
        email: true,
      }))
    } else {
      setFieldErrors((prev) => ({ ...prev, email: false }))
    }
  }

  const handlePhoneBlur = async (rawPhone) => {
    const phone = rawPhone.replace(/\D/g, '')
    if (!phone || phone.length !== 10) return

    const { exists, conflict } = await checkUserExists({ phone })
    if (exists && conflict === 'phone') {
      setFieldErrors((prev) => ({
        ...prev,
        phone: true,
      }))
    } else {
      setFieldErrors((prev) => ({ ...prev, phone: false }))
    }
  }

  const handleSubmit = async () => {
    setIsCreatingUser(true)
    try {
      const { username } = await signUpUser({
        firstName: firstName || 'No First Name',
        lastName: lastName || 'No Last Name',
        email: email,
        phone: phone,
      })
      onClose()
      await assumeUserAccount(username)
      window.location.href = '/admin/admin-inputs'
    } catch (error) {
      console.error('User creation failed:', error)
      toast.error('Failed to create user.')
    } finally {
      setIsCreatingUser(false)
    }
    const newUser = {
      firstName: firstName || 'No First Name',
      lastName: lastName || 'No Last Name',
      email: email || '',
      phone: phone || '',
    }

    //Send Welcome Email Function
    if (sendEmail) {
      const emailPayload = {
        email: email,
        phone: phone,
        parcoRep: adminDetails?.email,
      }
      await apiRequest('/api/users/sendWelcomeEmail', {
        method: 'POST',
        body: JSON.stringify(emailPayload),
      })
    }

    callZapier({
      user: intakeState,
      hookUrl: 'https://hooks.zapier.com/hooks/catch/15607509/2v2fj7y/',
      payload: {
        user: newUser,
        createdBy: `${adminDetails?.firstName} ${adminDetails?.lastName}`,
      },
      notificationMessage: sendEmail
        ? `You have successfully created a new user: ${firstName} ${lastName} and sent them a welcome email.`
        : `You have successfully created a new user: ${firstName} ${lastName} - NO welcome email sent.`,
      sendNotification: true,
    })
  }
  return (
    <Dialog
      open
      onClose={onClose}
      TransitionComponent={Transition}
      fullWidth
      maxWidth={'sm'}
      PaperProps={{ sx: { overflow: 'visible' } }}
    >
      <DialogContent sx={{ overflow: 'visible' }}>
        <motion.div
          key="scheduler"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
        >
          <Box
            sx={{
              justifyContent: 'space-between',
              position: 'relative',
              top: -64,
            }}
          >
            <IconButton
              onClick={onClose}
              sx={{
                position: 'absolute',
                top: 0,
                right: -16,
                color: '#fff',
                zIndex: 1,
                backgroundColor: '#000',
                '&:hover': {
                  backgroundColor: '#000',
                },
              }}
            >
              <Close />
            </IconButton>
          </Box>
          <Box p={3}>
            <Box>
              <Typography variant="headerTitle" sx={{ color: '#262627' }}>
                {title}
              </Typography>
            </Box>
            <Box mt={3}>
              <Grid container columnSpacing={3}>
                <Grid item xs={6} mb={2}>
                  {renderMuiFormField({
                    id: 'firstName',
                    required: true,
                    label: 'First Name',
                    placeholder: 'First Name',
                    inputType: 'text',
                    value: firstName,
                    onChange: setFirstName,
                  })}
                </Grid>
                <Grid item xs={6} mb={2}>
                  {renderMuiFormField({
                    id: 'lastName',
                    required: true,
                    label: 'Last Name',
                    placeholder: 'Last Name',
                    inputType: 'text',
                    value: lastName,
                    onChange: setLastName,
                  })}
                </Grid>
                <Grid item xs={12} mb={2}>
                  {renderMuiFormField({
                    id: 'email',
                    label: 'Email',
                    required: true,
                    placeholder: 'johnsmith@gmail.com',
                    inputType: 'email',
                    value: email,
                    onChange: setEmail,
                    onBlur: handleEmailBlur,
                    forceError: fieldErrors.email,
                  })}
                  {fieldErrors.email && (
                    <Typography
                      sx={{
                        color: 'red',
                        '& a': {
                          fontWeight: 600,
                          color: '#344767 !important',
                          textDecoration: 'underline',
                        },
                      }}
                      variant="inputNote"
                      component="p"
                    >
                      Email address is already in use.
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} mb={2}>
                  {renderMuiFormField({
                    id: 'phone',
                    required: true,
                    label: 'Phone Number',
                    placeholder: 'Eg. xxx-xxx-xxxx',
                    inputType: 'tel',
                    value: phone,
                    onChange: setPhone,
                    onBlur: handlePhoneBlur,
                    forceError: fieldErrors.phone,
                  })}
                  {fieldErrors.phone && (
                    <Typography
                      sx={{
                        color: 'red',
                        '& a': {
                          fontWeight: 600,
                          color: '#344767 !important',
                          textDecoration: 'underline',
                        },
                      }}
                      variant="inputNote"
                      component="p"
                    >
                      Phone number is already in use.
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} sx={{ marginLeft: '10px' }}>
                  <FormControlLabel
                    label="Send Welcome Email"
                    sx={{
                      '> span': {
                        fontWeight: 'normal !important',
                        fontSize: '1rem',
                      },
                    }}
                    control={
                      <Checkbox
                        name="sendEmail"
                        onChange={(e) => setSendEmail(e.target.checked)}
                        checked={checked}
                      />
                    }
                  ></FormControlLabel>
                </Grid>
              </Grid>
            </Box>
            <Box display="flex" justifyContent="space-between" mt={3}>
              <Button
                rel="noreferrer"
                variant="parcoRounded"
                color="outline"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                rel="noreferrer"
                variant="parcoRounded"
                disabled={isDisabled}
                onClick={handleSubmit}
                endIcon={
                  isCreatingUser && (
                    <CircularProgress size={20} color="inherit" />
                  )
                }
              >
                {isCreatingUser ? 'Creating User' : buttonText}
              </Button>
            </Box>
          </Box>
        </motion.div>
      </DialogContent>
    </Dialog>
  )
}
